/**
 * Solicitudes de demo ("Pedir demo" de /organizador): catálogos, reglas de
 * validación y utilidades puras.
 *
 * Las reglas son LAS MISMAS que aplica el servidor en
 * `backend/app/modules/leads/services.py` (cliente = servidor). Si cambias
 * una, cambia la otra. El formulario público y la bandeja del
 * superadministrador reutilizan este archivo.
 */

// ---------------------------------------------------------------------------
// Catálogos (las claves son las que acepta el servidor)
// ---------------------------------------------------------------------------

export type DemoSport = "tenis" | "padel" | "pickleball";
export type DemoOrgType = "club" | "academia" | "independiente" | "asociacion" | "otro";
export type DemoTypicalPlayers = "lt32" | "32_64" | "65_128" | "gt128" | "unknown";
export type DemoNextTournament = "this_month" | "1_3_months" | "later" | "no_date";
export type DemoRequestStatus = "nuevo" | "contactado" | "demo_agendada" | "cliente" | "descartado";
export type DemoEmailStatus = "pending" | "sent" | "failed" | "skipped";

export interface DemoOption<T extends string> {
  value: T;
  label: string;
}

/** Mismo orden y claves que `app/modules/sports` (tenis, pádel, pickleball). */
export const DEMO_SPORT_OPTIONS: ReadonlyArray<DemoOption<DemoSport>> = [
  { value: "tenis", label: "Tenis" },
  { value: "padel", label: "Pádel" },
  { value: "pickleball", label: "Pickleball" },
];

export const DEMO_ORG_TYPE_OPTIONS: ReadonlyArray<DemoOption<DemoOrgType>> = [
  { value: "club", label: "Club" },
  { value: "academia", label: "Academia" },
  { value: "independiente", label: "Organizador independiente" },
  { value: "asociacion", label: "Liga o asociación" },
  { value: "otro", label: "Otro" },
];

export const DEMO_TYPICAL_PLAYERS_OPTIONS: ReadonlyArray<DemoOption<DemoTypicalPlayers>> = [
  { value: "lt32", label: "Menos de 32" },
  { value: "32_64", label: "32 a 64" },
  { value: "65_128", label: "65 a 128" },
  { value: "gt128", label: "Más de 128" },
  { value: "unknown", label: "Aún no sé" },
];

export const DEMO_NEXT_TOURNAMENT_OPTIONS: ReadonlyArray<DemoOption<DemoNextTournament>> = [
  { value: "this_month", label: "Este mes" },
  { value: "1_3_months", label: "En 1 a 3 meses" },
  { value: "later", label: "Más adelante" },
  { value: "no_date", label: "Sin fecha aún" },
];

/** `label`: estado de una solicitud; `tab`: nombre de la pestaña (plural). */
export const DEMO_REQUEST_STATUS_OPTIONS: ReadonlyArray<
  DemoOption<DemoRequestStatus> & { tab: string }
> = [
  { value: "nuevo", label: "Nueva", tab: "Nuevas" },
  { value: "contactado", label: "Contactada", tab: "Contactadas" },
  { value: "demo_agendada", label: "Demo agendada", tab: "Demo agendada" },
  { value: "cliente", label: "Cliente", tab: "Clientes" },
  { value: "descartado", label: "Descartada", tab: "Descartadas" },
];

export const DEMO_EMAIL_STATUS_LABELS: Record<DemoEmailStatus, string> = {
  pending: "Enviando aviso",
  sent: "Aviso enviado",
  failed: "El aviso falló",
  skipped: "Sin aviso por correo",
};

export function demoOptionLabel<T extends string>(
  options: ReadonlyArray<DemoOption<T>>,
  value: string | null | undefined,
  fallback = "—",
): string {
  if (!value) return fallback;
  return options.find((option) => option.value === value)?.label ?? value;
}

// ---------------------------------------------------------------------------
// Contrato con la API
// ---------------------------------------------------------------------------

/** Cuerpo de `POST /api/v2/public/demo-requests`. */
export interface DemoRequestPayload {
  full_name: string;
  phone: string;
  email: string;
  city: string;
  sports: DemoSport[];
  org_type: DemoOrgType | null;
  org_name: string | null;
  typical_players: DemoTypicalPlayers | null;
  next_tournament: DemoNextTournament | null;
  message: string | null;
  consent: boolean;
  /** Trampa para robots: siempre "" desde el formulario real. */
  website: string;
  /** Milisegundos desde que se montó el formulario (menos de 3000 = robot). */
  elapsed_ms: number;
  source_path: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer_host: string | null;
}

/** Una solicitud tal como la regresa la bandeja del superadministrador. */
export interface DemoRequestItem {
  id: number;
  created_at: string | null;
  updated_at: string | null;
  full_name: string;
  phone: string;
  email: string;
  city: string;
  sports: DemoSport[];
  org_type: DemoOrgType | null;
  org_name: string | null;
  typical_players: DemoTypicalPlayers | null;
  next_tournament: DemoNextTournament | null;
  message: string | null;
  source_path: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referrer_host: string | null;
  consent_at: string | null;
  status: DemoRequestStatus;
  notes: string | null;
  contacted_at: string | null;
  email_status: DemoEmailStatus;
}

export type DemoRequestCounts = Record<DemoRequestStatus, number>;

// ---------------------------------------------------------------------------
// Reglas de validación (cliente = servidor)
// ---------------------------------------------------------------------------

export const DEMO_REQUEST_LIMITS = {
  nameMin: 2,
  nameMax: 120,
  cityMin: 2,
  cityMax: 120,
  emailMax: 254,
  orgNameMax: 160,
  messageMax: 1000,
  notesMax: 4000,
  minElapsedMs: 3000,
} as const;

export const DEMO_REQUEST_MESSAGES = {
  full_name: "Escribe tu nombre.",
  phone: "Escribe tu celular a 10 dígitos.",
  email: "Escribe un correo válido, por ejemplo nombre@club.com.",
  city: "Escribe tu ciudad.",
  sports: "Elige al menos un deporte.",
  consent: "Necesitamos tu autorización para contactarte.",
  option: "Elige una opción de la lista.",
  org_name: `Usa ${DEMO_REQUEST_LIMITS.orgNameMax} caracteres como máximo.`,
  message: `Usa ${DEMO_REQUEST_LIMITS.messageMax} caracteres como máximo.`,
  textInvalid: "Este texto tiene caracteres que no podemos guardar.",
  network: "No pudimos enviar tu solicitud. Revisa tu conexión e inténtalo de nuevo.",
  rateLimited: "Recibimos varias solicitudes desde esta conexión. Intenta de nuevo en una hora.",
} as const;

/** Campos del formulario que pueden traer un error propio. */
export type DemoRequestField =
  | "full_name"
  | "phone"
  | "email"
  | "city"
  | "sports"
  | "org_type"
  | "org_name"
  | "typical_players"
  | "next_tournament"
  | "message"
  | "consent";

export type DemoRequestFieldErrors = Partial<Record<DemoRequestField, string>>;

/** Orden de los campos en pantalla (para llevar el foco al primer error). */
export const DEMO_REQUEST_FIELD_ORDER: ReadonlyArray<DemoRequestField> = [
  "full_name",
  "phone",
  "email",
  "city",
  "sports",
  "org_type",
  "org_name",
  "typical_players",
  "next_tournament",
  "message",
  "consent",
];

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** Surrogates sueltos (texto mal formado): el servidor no los puede guardar. */
const LONE_SURROGATE = /\p{Cs}/u;
const LONE_SURROGATES = /\p{Cs}/gu;
/** Control, formato (p. ej. U+202E), surrogates, uso privado, sin asignar. */
const OTHER_CHARS = /\p{C}/u;

/**
 * Caracteres que no se aceptan en el correo (mismo conjunto que el
 * servidor): permitirían inyectar parámetros en un enlace `mailto:`
 * (`?bcc=`, `%40`, `&cc=`) o romper el HTML del aviso.
 */
export const DEMO_EMAIL_FORBIDDEN_CHARS = "?&%/#\"'<>`\\,;:()[]";

/** ¿El texto tiene surrogates sueltos (no se puede guardar)? */
export function hasUnsavableChars(value: string | null | undefined): boolean {
  return typeof value === "string" && LONE_SURROGATE.test(value);
}

/** Largo en caracteres reales (como `len()` de Python), no en unidades UTF-16. */
function charLength(value: string): number {
  return Array.from(value).length;
}

/** Texto de una línea: sin caracteres de control y con espacios colapsados. */
export function cleanSingleLine(value: string): string {
  return stripEdges(value.replace(CONTROL_CHARS, "").replace(/\s+/g, " "));
}

/** Texto de varias líneas: sin caracteres de control y sin espacios ni
 * caracteres invisibles en los extremos. */
export function cleanMultiLine(value: string): string {
  return stripEdges(value.replace(/\r\n?/g, "\n").replace(CONTROL_CHARS, ""));
}

const EDGES = /^[\s\p{Cf}]+|[\s\p{Cf}]+$/gu;

/**
 * Quita espacios Y caracteres de formato (categoría Cf: BOM, espacio de
 * ancho cero, marcas de dirección…) al principio y al final, igual que
 * `strip_edges` del servidor. `trim()` no basta: no quita U+200B.
 */
export function stripEdges(value: string): string {
  return value.replace(EDGES, "");
}

/** Las notas como las guarda el servidor (vacías = null). */
export function normalizeDemoNotes(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return cleanMultiLine(value) || null;
}

/** El correo como lo guarda el servidor: sin bordes invisibles y en minúsculas. */
export function normalizeDemoEmail(raw: string): string {
  return stripEdges(raw).toLowerCase();
}

/**
 * Celular mexicano a 10 dígitos, o null si no se puede.
 *
 * Se quita todo lo que no sea dígito; si quedan 12 y empiezan con 52 se
 * quita el 52; si quedan 13 y empiezan con 521 se quita el 521; deben quedar
 * exactamente 10. `+52 1 33 1234 5678` → `3312345678`.
 */
export function normalizeMxPhone(raw: string | number | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  let digits = String(raw).replace(/[^0-9]/g, "");
  if (digits.length === 12 && digits.startsWith("52")) {
    digits = digits.slice(2);
  } else if (digits.length === 13 && digits.startsWith("521")) {
    digits = digits.slice(3);
  }
  return digits.length === 10 ? digits : null;
}

/** Formato `algo@dominio.ext`, máximo 254 caracteres (se compara en minúsculas). */
export function isValidDemoEmail(raw: string): boolean {
  if (hasUnsavableChars(raw)) return false;
  const email = normalizeDemoEmail(raw);
  if (email.length === 0 || charLength(email) > DEMO_REQUEST_LIMITS.emailMax) return false;
  if (!EMAIL_RE.test(email)) return false;
  if (Array.from(email).some((ch) => DEMO_EMAIL_FORBIDDEN_CHARS.includes(ch))) return false;
  return !OTHER_CHARS.test(email);
}

/** Lo que el visitante captura (los datos ocultos no se validan). */
export interface DemoRequestFormValues {
  full_name: string;
  phone: string;
  email: string;
  city: string;
  sports: ReadonlyArray<string>;
  org_type: string | null;
  org_name: string | null;
  typical_players: string | null;
  next_tournament: string | null;
  message: string | null;
  consent: boolean;
}

function checkOption<T extends string>(
  options: ReadonlyArray<DemoOption<T>>,
  value: string | null,
): boolean {
  if (value === null || stripEdges(value) === "") return true;
  return options.some((option) => option.value === stripEdges(value));
}

/** Errores por campo con los mismos mensajes que regresa el servidor. `{}` = válido. */
export function validateDemoRequest(values: DemoRequestFormValues): DemoRequestFieldErrors {
  const errors: DemoRequestFieldErrors = {};
  const limits = DEMO_REQUEST_LIMITS;

  const name = cleanSingleLine(values.full_name ?? "");
  if (
    hasUnsavableChars(values.full_name) ||
    charLength(name) < limits.nameMin ||
    charLength(name) > limits.nameMax
  ) {
    errors.full_name = DEMO_REQUEST_MESSAGES.full_name;
  }
  if (normalizeMxPhone(values.phone) === null) {
    errors.phone = DEMO_REQUEST_MESSAGES.phone;
  }
  if (!isValidDemoEmail(values.email ?? "")) {
    errors.email = DEMO_REQUEST_MESSAGES.email;
  }
  const city = cleanSingleLine(values.city ?? "");
  if (
    hasUnsavableChars(values.city) ||
    charLength(city) < limits.cityMin ||
    charLength(city) > limits.cityMax
  ) {
    errors.city = DEMO_REQUEST_MESSAGES.city;
  }
  const sportKeys = DEMO_SPORT_OPTIONS.map((option) => option.value as string);
  const sports = values.sports ?? [];
  if (sports.length === 0 || !sports.every((sport) => sportKeys.includes(stripEdges(sport)))) {
    errors.sports = DEMO_REQUEST_MESSAGES.sports;
  }
  if (!checkOption(DEMO_ORG_TYPE_OPTIONS, values.org_type)) {
    errors.org_type = DEMO_REQUEST_MESSAGES.option;
  }
  if (!checkOption(DEMO_TYPICAL_PLAYERS_OPTIONS, values.typical_players)) {
    errors.typical_players = DEMO_REQUEST_MESSAGES.option;
  }
  if (!checkOption(DEMO_NEXT_TOURNAMENT_OPTIONS, values.next_tournament)) {
    errors.next_tournament = DEMO_REQUEST_MESSAGES.option;
  }
  if (hasUnsavableChars(values.org_name)) {
    errors.org_name = DEMO_REQUEST_MESSAGES.textInvalid;
  } else if (values.org_name && charLength(cleanSingleLine(values.org_name)) > limits.orgNameMax) {
    errors.org_name = DEMO_REQUEST_MESSAGES.org_name;
  }
  if (hasUnsavableChars(values.message)) {
    errors.message = DEMO_REQUEST_MESSAGES.textInvalid;
  } else if (values.message && charLength(cleanMultiLine(values.message)) > limits.messageMax) {
    errors.message = DEMO_REQUEST_MESSAGES.message;
  }
  if (values.consent !== true) {
    errors.consent = DEMO_REQUEST_MESSAGES.consent;
  }
  return errors;
}

/** Primer campo con error en el orden de la pantalla (para mover el foco). */
export function firstDemoRequestErrorField(
  errors: DemoRequestFieldErrors,
): DemoRequestField | null {
  return DEMO_REQUEST_FIELD_ORDER.find((field) => Boolean(errors[field])) ?? null;
}

// ---------------------------------------------------------------------------
// Datos ocultos del formulario
// ---------------------------------------------------------------------------

/** Igual que el servidor: sin caracteres que no se pueden guardar y el
 * recorte al final. */
function cleanHidden(value: string | null | undefined, max: number): string | null {
  if (!value) return null;
  const clean = cleanSingleLine(value.replace(LONE_SURROGATES, ""));
  return clean ? Array.from(clean).slice(0, max).join("") : null;
}

/** Solo el host de `document.referrer`, o null. */
export function referrerHostOf(referrer: string | null | undefined): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    return host ? host.slice(0, 120) : null;
  } catch {
    return null;
  }
}

/** Datos ocultos que acompañan el envío (página, UTM, referencia, tiempo). */
export function demoRequestContext(params: {
  pathname: string;
  search: string;
  referrer: string | null | undefined;
  elapsedMs: number;
}): Pick<
  DemoRequestPayload,
  "source_path" | "utm_source" | "utm_medium" | "utm_campaign" | "referrer_host" | "elapsed_ms"
> {
  const query = new URLSearchParams(params.search);
  return {
    source_path: cleanHidden(params.pathname, 200) ?? "",
    utm_source: cleanHidden(query.get("utm_source"), 100),
    utm_medium: cleanHidden(query.get("utm_medium"), 100),
    utm_campaign: cleanHidden(query.get("utm_campaign"), 100),
    referrer_host: referrerHostOf(params.referrer),
    elapsed_ms: Math.max(0, Math.round(params.elapsedMs)),
  };
}

// ---------------------------------------------------------------------------
// Errores de la API → qué mostrar en el formulario
// ---------------------------------------------------------------------------

export type DemoRequestErrorView =
  | { kind: "validation"; fieldErrors: DemoRequestFieldErrors; message: string }
  | { kind: "rate_limited"; message: string }
  | { kind: "network"; message: string };

function isFieldKey(value: string): value is DemoRequestField {
  return (DEMO_REQUEST_FIELD_ORDER as ReadonlyArray<string>).includes(value);
}

/**
 * Traduce el error de `api.publicDemoRequests.create` (un `ApiError`) a lo
 * que muestra el formulario:
 * - 422 `validation_error` → mensaje en cada campo (`details.errors` es
 *   `{campo: mensaje}`).
 * - 429 `demo_rate_limited` → aviso de "intenta en una hora".
 * - Red, 5xx o cualquier otra cosa → aviso genérico de conexión.
 */
export function mapDemoRequestError(error: unknown): DemoRequestErrorView {
  const candidate = (error ?? {}) as {
    status?: unknown;
    code?: unknown;
    message?: unknown;
    details?: unknown;
  };
  const status = typeof candidate.status === "number" ? candidate.status : 0;
  if (status === 429 || candidate.code === "demo_rate_limited") {
    // El mensaje de NUESTRO servidor (código demo_rate_limited) ya viene en
    // español; un 429 de otro lado (proxy, cuerpo no JSON) trae un texto
    // genérico en inglés, así que ahí se usa el texto fijo.
    const serverMessage =
      candidate.code === "demo_rate_limited" && typeof candidate.message === "string"
        ? candidate.message.trim()
        : "";
    return { kind: "rate_limited", message: serverMessage || DEMO_REQUEST_MESSAGES.rateLimited };
  }
  if (status === 422) {
    const details = (candidate.details ?? {}) as { errors?: unknown };
    const fieldErrors: DemoRequestFieldErrors = {};
    if (details.errors && typeof details.errors === "object" && !Array.isArray(details.errors)) {
      for (const [field, message] of Object.entries(details.errors as Record<string, unknown>)) {
        if (isFieldKey(field) && typeof message === "string" && message) {
          fieldErrors[field] = message;
        }
      }
    }
    if (Object.keys(fieldErrors).length > 0) {
      return {
        kind: "validation",
        fieldErrors,
        message: typeof candidate.message === "string" ? candidate.message : "Revisa los datos marcados.",
      };
    }
  }
  return { kind: "network", message: DEMO_REQUEST_MESSAGES.network };
}

// ---------------------------------------------------------------------------
// Contacto desde la bandeja del superadministrador
// ---------------------------------------------------------------------------

/** Primer nombre para saludar ("Laura Hernández" → "Laura"). */
export function firstName(fullName: string): string {
  return cleanSingleLine(fullName ?? "").split(" ")[0] ?? "";
}

/** Enlace de WhatsApp con el saludo ya escrito. */
export function buildDemoWhatsAppUrl(request: {
  phone: string;
  full_name: string;
  org_name?: string | null;
}): string {
  const phone = normalizeMxPhone(request.phone) ?? String(request.phone ?? "").replace(/[^0-9]/g, "");
  const name = firstName(request.full_name);
  const org = request.org_name ? cleanSingleLine(request.org_name) : "";
  const greeting = name ? `Hola ${name}` : "Hola";
  const text =
    `${greeting}, soy Eduardo de Torneos123. Recibí tu solicitud de demo` +
    `${org ? ` para ${org}` : ""}. ¿Qué día te acomoda?`;
  return `https://wa.me/52${phone}?text=${encodeURIComponent(text)}`;
}

/** Enlace para responder por correo. */
export function buildDemoMailtoUrl(email: string): string {
  return `mailto:${encodeURIComponent(email.trim()).replace(/%40/g, "@")}?subject=${encodeURIComponent(
    "Tu demo de Torneos123",
  )}`;
}
